<template>
  <AppShell
    active="command"
    :workspace="workspace"
    title="Command center"
    subtitle="A focused operating overview for live acquisition work, blockers, and next actions."
    max-width-class="max-w-none"
    data-testid="command-center-page"
  >
    <template #actions>
      <a href="/opportunities" class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-land-primary">
        <Plus class="size-4" aria-hidden="true" />
        Source opportunity
      </a>
    </template>

    <section class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" aria-label="Workspace metrics">
      <article v-for="metric in metrics" :key="metric.key" class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-sm font-medium text-land-ink-muted">{{ metric.label }}</p>
            <p class="mt-2 text-2xl font-semibold tracking-normal text-land-ink">{{ formatNumber(metric.value) }}</p>
          </div>
          <div class="grid size-9 place-items-center rounded-md" :class="metricToneClasses(metric.tone)">
            <component :is="metricIcon(metric.key)" class="size-4" aria-hidden="true" />
          </div>
        </div>
        <p class="mt-3 text-xs leading-5 text-land-ink-soft">{{ metric.detail }}</p>
      </article>
    </section>

    <section class="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div class="space-y-4">
        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="flex items-center justify-between gap-3 border-b border-land-border-soft p-4">
            <div>
              <h2 class="text-lg font-semibold tracking-normal">Recent opportunities</h2>
              <p class="mt-1 text-sm text-land-ink-muted">Latest records that need acquisition attention.</p>
            </div>
            <a href="/opportunities" class="text-sm font-semibold text-land-primary hover:text-land-primary-hover">Open pipeline</a>
          </div>
          <div class="divide-y divide-land-border-soft">
            <a v-for="opportunity in opportunities.slice(0, 6)" :key="opportunity.id" :href="`/opportunities/${opportunity.id}`" class="grid gap-3 px-4 py-3 hover:bg-land-surface-muted md:grid-cols-[minmax(0,1fr)_104px_96px] md:items-center">
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-land-ink">{{ opportunity.name }}</p>
                <p class="mt-1 truncate text-xs text-land-ink-soft">{{ opportunity.market }} / {{ opportunity.county }}, {{ opportunity.state }}</p>
                <div class="mt-2 flex flex-wrap gap-1.5">
                  <StateBadge :label="opportunity.health?.stateLabel || 'Unknown'" :tone="opportunity.health?.tone || 'slate'" />
                  <StateBadge :label="opportunity.screeningSummary?.statusLabel || 'Not run'" :tone="opportunity.screeningSummary?.statusTone || 'slate'" />
                  <StateBadge v-if="opportunity.taskSummary?.overdue" :label="`${opportunity.taskSummary.overdue} overdue`" tone="red" />
                </div>
              </div>
              <span class="inline-flex h-5 max-w-full items-center justify-center justify-self-start whitespace-nowrap rounded bg-land-primary-soft px-2 text-[11px] font-semibold leading-none text-land-primary ring-1 ring-inset ring-land-primary/15 md:justify-self-center">{{ opportunity.stageLabel }}</span>
              <p class="text-sm text-land-ink-muted md:text-right">{{ formatNumber(opportunity.acreage) }} acres</p>
            </a>
            <div v-if="opportunities.length === 0" class="px-4 py-10 text-center">
              <p class="text-sm font-semibold">No opportunities yet</p>
              <a href="/opportunities" class="mt-3 inline-flex h-9 items-center rounded-md bg-land-primary px-3 text-sm font-semibold text-white">Create the first one</a>
            </div>
          </div>
        </section>
      </div>

      <aside class="space-y-4">
        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <h2 class="text-lg font-semibold tracking-normal">Risk queue</h2>
            <p class="mt-1 text-sm text-land-ink-muted">Blocked diligence and overdue work.</p>
          </div>
          <div class="divide-y divide-land-border-soft">
            <a v-for="item in riskQueue" :key="`${item.type}-${item.id}`" :href="`/opportunities/${item.opportunityId}`" class="block px-4 py-3 hover:bg-land-surface-muted">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">{{ item.title }}</p>
                  <p class="mt-1 truncate text-xs text-land-ink-soft">{{ item.opportunity }} · {{ item.assignee }}</p>
                </div>
                <StateBadge :label="item.badge" :tone="item.tone" />
              </div>
            </a>
            <div v-if="riskQueue.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No blocked or overdue work.</div>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <h2 class="text-lg font-semibold tracking-normal">Next actions</h2>
            <p class="mt-1 text-sm text-land-ink-muted">Open tasks due soon.</p>
          </div>
          <div class="divide-y divide-land-border-soft">
            <article v-for="task in tasks.slice(0, 5)" :key="task.id" class="px-4 py-3">
              <p class="truncate text-sm font-medium">{{ task.title }}</p>
              <p class="mt-1 truncate text-xs text-land-ink-soft">{{ task.opportunity }} · {{ task.assignee }}</p>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <StateBadge :label="task.dueLabel" :tone="task.dueTone || 'slate'" />
                <StateBadge :label="task.priorityLabel" :tone="task.priorityTone || 'slate'" />
              </div>
            </article>
            <div v-if="tasks.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No open tasks.</div>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm">
          <h2 class="text-lg font-semibold tracking-normal">Map coverage</h2>
          <p class="mt-1 text-sm text-land-ink-muted">Review opportunities with parcel coordinates.</p>
          <a href="/map" class="mt-4 inline-flex h-9 items-center gap-2 rounded-md border border-land-border px-3 text-sm font-semibold text-land-ink-muted hover:bg-land-surface-muted">
            <MapPinned class="size-4" aria-hidden="true" />
            Open map
          </a>
        </section>
      </aside>
    </section>
  </AppShell>
</template>

<script setup>
import { AlertTriangle, CalendarClock, Command, MapPinned, Plus, Zap } from '@lucide/vue'
import { computed } from 'vue'
import AppShell from '@/components/shared/app_shell.vue'
import StateBadge from '@/components/shared/state_badge.vue'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

const workspace = computed(() => props.payload.workspace || {})
const metrics = computed(() => props.payload.metrics || [])
const opportunities = computed(() => props.payload.opportunities || [])
const tasks = computed(() => props.payload.tasks || [])
const workQueues = computed(() => props.payload.workQueues || {})
const riskQueue = computed(() => [
  ...(workQueues.value.blockedDiligence || []).map((item) => ({
    id: item.id,
    type: 'diligence',
    title: item.title,
    opportunity: item.opportunity,
    opportunityId: item.opportunityId,
    assignee: item.assignee,
    badge: item.statusLabel,
    tone: item.statusTone || 'red'
  })),
  ...(workQueues.value.overdueTasks || []).map((task) => ({
    id: task.id,
    type: 'task',
    title: task.title,
    opportunity: task.opportunity,
    opportunityId: task.opportunityId,
    assignee: task.assignee,
    badge: task.dueLabel,
    tone: task.dueTone || 'red'
  }))
].slice(0, 6))

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))

const metricIcon = (key) => ({
  open_opportunities: Zap,
  tracked_parcels: MapPinned,
  active_blockers: AlertTriangle,
  upcoming_tasks: CalendarClock
})[key] || Command

const metricToneClasses = (tone) => ({
  primary: 'bg-land-primary-soft text-land-primary',
  blue: 'bg-blue-50 text-blue-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
  green: 'bg-emerald-50 text-emerald-700',
  slate: 'bg-slate-100 text-slate-700'
})[tone] || 'bg-land-surface-soft text-land-ink-muted'
</script>
